import type {
  AlarmState,
  BypassState,
  CockpitState,
  PgmItem,
  PgmState,
  QueueState,
  RegistrySummary,
  SirenState,
  TroubleState,
} from "../api";
import type { Tab } from "../ui-contract";
import {
  R8CheckIcon,
  R8ChevronRightIcon,
  R8Icon,
  R8RefreshIcon,
} from "./icons";
import {
  selectAreaPresentation,
  selectBypassPresentation,
  selectCockpitRiskPresentation,
  selectPgmPresentation,
  selectSirenPresentation,
} from "./presentation";
import "./views.css";

interface NavigateProps {
  onNavigate: (tab: Tab) => void;
}

function ViewTitle({ eyebrow, title, aside }: { eyebrow: string; title: string; aside?: React.ReactNode }) {
  return (
    <div className="r8-view-title">
      <div><span>{eyebrow}</span><h1>{title}</h1></div>
      {aside}
    </div>
  );
}

function formatEventTime(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function durationAccessibleLabel(prefix: string, milliseconds: number): string {
  if (milliseconds < 1_000) return `${prefix} por ${milliseconds} milissegundos`;
  const seconds = milliseconds / 1_000;
  return `${prefix} por ${seconds} ${seconds === 1 ? "segundo" : "segundos"}`;
}

function queueCountLabel(key: string): string {
  const labels: Record<string, string> = {
    COMPLETED: "Concluídos",
    DEAD_LETTER: "Dead-letter histórico",
    FAILED: "Falhas",
    PENDING: "Pendentes",
    PROCESSING: "Em processamento",
  };
  return labels[key.toUpperCase()] ?? key.replace(/_/g, " ");
}

function localizedTechnicalState(value: string): string {
  const labels: Record<string, string> = {
    BLOCKED: "BLOQUEADO",
    FRESH: "ATUAL",
    HEALTHY: "SAUDÁVEL",
    PASSED: "APROVADO",
    RUNNING: "EM EXECUÇÃO",
    STOPPED: "PARADA",
  };
  return labels[value.trim().toUpperCase()] ?? value;
}

export function R8CockpitView({
  alarm,
  cockpit,
  pgms,
  siren,
  bypass,
  troubles,
  onNavigate,
  r14Mode = false,
}: {
  alarm: AlarmState;
  cockpit: CockpitState;
  pgms: PgmState;
  siren: SirenState;
  bypass: BypassState;
  troubles: TroubleState;
  r14Mode?: boolean;
} & NavigateProps) {
  const {
    protectedZones,
    totalZones,
    activeOutputs,
    tone,
    title,
    summary,
    actionLabel,
    actionTab,
    noImmediateAction,
  } = selectCockpitRiskPresentation({ alarm, cockpit, pgms, siren, bypass, troubles });

  return (
    <>
      <section className="r8-risk-hero" data-tone={tone}>
        <span className="r8-kicker">SITUAÇÃO ATUAL</span>
        <div className="r8-risk-hero__body">
          <div><h1>{title}</h1><p>{summary}</p></div>
          <span className="r8-risk-hero__symbol" aria-hidden="true"><R8Icon icon="shield" /></span>
        </div>
        <button type="button" onClick={() => onNavigate(actionTab)}>{actionLabel}<R8ChevronRightIcon /></button>
      </section>

      <section className="r8-metric-grid" aria-label="Resumo operacional">
        <div data-tone="armed"><strong>{protectedZones}<small>/{totalZones}</small></strong><span>Zonas protegidas</span></div>
        <div data-tone={troubles.counts.open ? "failure" : "armed"}><strong>{troubles.counts.open}</strong><span>Falhas abertas</span></div>
        <div data-tone={bypass.active.length ? "inhibited" : "armed"}><strong>{bypass.active.length}</strong><span>Bypasses</span></div>
        <div data-tone={siren.siren.state === "ON" ? "alarm" : activeOutputs ? "neutral" : "disarmed"}><strong>{activeOutputs}</strong><span>Saídas lógicas ativas</span></div>
      </section>

      <section className="r8-decision-card">
        <div><span className="r8-kicker">PRÓXIMA AÇÃO</span><strong>{noImmediateAction ? "Nenhuma ação imediata" : actionLabel}</strong></div>
        {noImmediateAction
          ? <span className="r8-decision-card__quiet" aria-hidden="true"><R8CheckIcon /></span>
          : <button type="button" onClick={() => onNavigate(actionTab)} aria-label={`${actionLabel} na navegação`}><R8ChevronRightIcon /></button>}
      </section>

      <section className="r8-panel">
        <div className="r8-panel__heading"><div><span className="r8-kicker">ATIVIDADE REAL</span><h2>Eventos relevantes</h2></div><button type="button" onClick={() => onNavigate("EVENTS")}>VER TODOS</button></div>
        {troubles.active.length > 0 ? (
          <ol className="r8-mini-timeline">
            {troubles.active.slice(0, 3).map((item) => (
              <li key={item.trouble_id} data-tone="failure">
                <time>{formatEventTime(item.updated_at_utc ?? item.opened_at_utc)}</time>
                <div><strong>{item.message}</strong><span>{item.resource_id} · {item.status}</span></div>
              </li>
            ))}
          </ol>
        ) : (
          <div className="r8-quiet-state"><span aria-hidden="true"><R8CheckIcon /></span><div><strong>{r14Mode ? "Sem falha ativa" : "Sem Trouble ativo"}</strong><p>A timeline permanece vazia; nenhum evento ilustrativo foi inserido.</p></div></div>
        )}
      </section>
    </>
  );
}

export function R8ZonesView({
  alarm,
  bypass,
  reason,
  canOperate,
  commandsLocked,
  onReasonChange,
  onOperateAll,
  onOperateArea,
  onOperateBypass,
  onRefresh,
  r14Mode = false,
}: {
  alarm: AlarmState;
  bypass: BypassState;
  reason: string;
  canOperate: boolean;
  commandsLocked: boolean;
  onReasonChange: (value: string) => void;
  onOperateAll: (targetState: "ARMED" | "DISARMED") => void;
  onOperateArea: (area: AlarmState["areas"][number]) => void;
  onOperateBypass: (zone: number) => void;
  onRefresh: () => void;
  r14Mode?: boolean;
}) {
  const activeBypass = new Map(bypass.active.map((item) => [item.zone, item]));
  const allArmed = alarm.global_state === "ARMED_ALL"
    && alarm.areas.length > 0
    && alarm.areas.every((area) => area.state === "ARMED");
  const allDisarmed = alarm.global_state === "DISARMED_ALL"
    && alarm.areas.length > 0
    && alarm.areas.every((area) => area.state === "DISARMED");

  return (
    <>
      <ViewTitle eyebrow="PROTEÇÃO" title="Zonas" aside={<button className="r8-icon-action" type="button" onClick={onRefresh} aria-label="Atualizar zonas"><R8RefreshIcon /></button>} />
      <p className="r8-view-intro">Estado operacional derivado somente da partição e do bypass autoritativos. Mapa e dados individuais ausentes não são simulados.</p>
      <label className="r8-reason-field"><span>Motivo para comandos de proteção</span><input value={reason} maxLength={500} onChange={(event) => onReasonChange(event.target.value)} /></label>

      <section className="r8-area-panel" aria-label="Áreas e estado global">
        <div className="r8-area-panel__global">
          <span>ESTADO GLOBAL · v{alarm.state_version}</span>
          <strong>{alarm.global_label}</strong>
        </div>
        <div className="r8-area-grid">
          {alarm.areas.map((area) => {
            const presentation = selectAreaPresentation(area);
            const target = area.state === "ARMED" ? "desarmar" : "armar";
            return (
              <button
                type="button"
                key={area.area}
                data-tone={presentation.tone}
                disabled={!canOperate || commandsLocked}
                onClick={() => onOperateArea(area)}
                aria-label={`${area.partition_id} ${area.display_name}: ${presentation.stateLabel}; tocar para ${target}`}
              >
                <span>{area.partition_id}</span>
                <strong>{area.display_name}</strong>
                <small>{presentation.stateLabel}</small>
              </button>
            );
          })}
        </div>
        <div className="r8-global-actions">
          <button type="button" disabled={!canOperate || commandsLocked || allArmed} onClick={() => onOperateAll("ARMED")}>ARMAR TUDO</button>
          <button type="button" data-action="disarm" disabled={!canOperate || commandsLocked || allDisarmed} onClick={() => onOperateAll("DISARMED")}>DESARMAR TUDO</button>
        </div>
      </section>

      <div className={r14Mode ? "r14-zone-grid" : "r8-zone-list"} aria-label={r14Mode ? "Zonas e bypass" : undefined}>
        {Array.from({ length: 15 }, (_, index) => index + 1).map((zone) => {
          const area = alarm.areas.find((item) => item.zones.includes(zone));
          const active = activeBypass.get(zone);
          const bypassState = selectBypassPresentation(active ?? null);
          const areaPresentation = selectAreaPresentation(area);
          const tone = active ? "inhibited" : areaPresentation.tone;
          const protection = active ? "INIBIDA" : area?.state === "ARMED" ? "ARMADA" : area?.state === "DISARMED" ? "DESARMADA" : "SEM CONFIRMAÇÃO";

          return r14Mode ? (
            <button
              className="r14-zone-tile"
              type="button"
              data-tone={tone}
              data-bypass={Boolean(active)}
              key={zone}
              disabled={!canOperate || commandsLocked}
              onClick={() => onOperateBypass(zone)}
              aria-label={`Zona ${zone}, ${area?.partition_id ?? "partição indisponível"}, ${protection}, ${bypassState.stateLabel}; ${active ? "restaurar bypass" : "aplicar bypass"}`}
            >
              <span className="r14-zone-tile__id">Z{String(zone).padStart(2, "0")}</span>
              <strong>{active ? "BYPASS" : protection}</strong>
              <small>{area?.partition_id ?? "—"}</small>
              <i aria-hidden="true" />
            </button>
          ) : (
            <article className="r8-zone-card" data-tone={tone} key={zone}>
              <div className="r8-zone-card__identity">
                <strong>Z{String(zone).padStart(2, "0")}</strong>
                <span>{area?.partition_id ?? "—"} · {area?.display_name ?? "Partição indisponível"}</span>
              </div>
              <div className="r8-zone-card__state">
                <strong>{protection}</strong>
                <span>Saúde {area?.health ?? "não informada"} · {bypassState.stateLabel}</span>
              </div>
              <button
                type="button"
                disabled={!canOperate || commandsLocked}
                onClick={() => onOperateBypass(zone)}
                aria-label={`${active ? "Restaurar" : "Aplicar bypass em"} zona ${zone}`}
              >
                {active ? "RESTAURAR" : "BYPASS"}
              </button>
            </article>
          );
        })}
      </div>
    </>
  );
}

export function R8AutomationView({
  pgms,
  siren,
  reason,
  pgmDuration,
  sirenDuration,
  canOperate,
  commandsLocked,
  onReasonChange,
  onPgmDurationChange,
  onSirenDurationChange,
  onOperatePgm,
  onOperateSiren,
  r14Mode = false,
}: {
  pgms: PgmState;
  siren: SirenState;
  reason: string;
  pgmDuration: number;
  sirenDuration: number;
  canOperate: boolean;
  commandsLocked: boolean;
  onReasonChange: (value: string) => void;
  onPgmDurationChange: (value: number) => void;
  onSirenDurationChange: (value: number) => void;
  onOperatePgm: (pgm: PgmItem) => void;
  onOperateSiren: () => void;
  r14Mode?: boolean;
}) {
  const sirenPresentation = selectSirenPresentation(siren.siren);
  return (
    <>
      <ViewTitle eyebrow="AUTOMAÇÃO" title="Saídas" aside={<span className="r8-readback-badge">{pgms.physical_output_allowed ? "FÍSICO · READBACK" : "SIMULADO"}</span>} />
      <p className="r8-view-intro">PGM01–03 e SIREN_MAIN permanecem recursos independentes. Nenhuma função de holofote ou câmera foi inferida.</p>

      <label className="r8-reason-field"><span>Motivo operacional obrigatório</span><input value={reason} maxLength={500} onChange={(event) => onReasonChange(event.target.value)} /></label>

      <section className="r8-duration-panel" aria-label="Duração temporizada">
        <div role="group" aria-label="Duração da PGM"><span>PGM</span>{[1000, 3000, 5000, 15000, 30000].map((value) => <button type="button" key={value} data-active={pgmDuration === value} aria-pressed={pgmDuration === value} aria-label={durationAccessibleLabel("PGM", value)} onClick={() => onPgmDurationChange(value)}>{value / 1000}s</button>)}</div>
        <div role="group" aria-label="Duração da sirene"><span>SIRENE</span>{[250, 500, 1000, 3000, 5000].map((value) => <button type="button" key={value} data-active={sirenDuration === value} aria-pressed={sirenDuration === value} aria-label={durationAccessibleLabel("Sirene", value)} onClick={() => onSirenDurationChange(value)}>{value < 1000 ? `${value}ms` : `${value / 1000}s`}</button>)}</div>
      </section>

      <div className={r14Mode ? "r14-output-grid" : "r8-output-list"} aria-label={r14Mode ? "Saídas lógicas e sirene" : undefined}>
        {pgms.pgms.map((pgm) => {
          const presentation = selectPgmPresentation(pgm, "GENERIC_LOGICAL");
          const on = pgm.state === "ON";
          return r14Mode ? (
            <button
              className="r14-output-tile"
              type="button"
              data-tone={presentation.tone}
              data-state={pgm.state.toLowerCase()}
              key={pgm.pgm_id}
              disabled={!canOperate || commandsLocked}
              onClick={() => onOperatePgm(pgm)}
              aria-label={`${pgm.pgm_id}: ${on ? "desligar" : durationAccessibleLabel("acionar", pgmDuration)}. Estado ${presentation.stateLabel}`}
            >
              <span className="r14-output-tile__id">{pgm.pgm_id.slice(-2)}</span>
              <span className="r14-output-tile__kind">PGM</span>
              <strong>{presentation.stateLabel}</strong>
              <small>{pgm.auto_off_at_utc ? `OFF ${formatEventTime(pgm.auto_off_at_utc)}` : `${pgmDuration / 1_000}s`}</small>
              <i aria-hidden="true" />
            </button>
          ) : (
            <article className="r8-output-card" data-tone={presentation.tone} key={pgm.pgm_id}>
              <div className="r8-output-card__icon" aria-hidden="true">{pgm.pgm_id.slice(-1)}</div>
              <div><span>SAÍDA LÓGICA</span><h2>{pgm.pgm_id}</h2><p>{presentation.stateLabel}{pgm.auto_off_at_utc ? ` · auto-OFF ${formatEventTime(pgm.auto_off_at_utc)}` : ""}</p></div>
              <button type="button" data-action={on ? "off" : "on"} disabled={!canOperate || commandsLocked} onClick={() => onOperatePgm(pgm)} aria-label={`${pgm.pgm_id}: ${on ? "desligar" : durationAccessibleLabel("acionar", pgmDuration)}`}>{on ? "DESLIGAR" : `ACIONAR ${pgmDuration / 1000}s`}</button>
            </article>
          );
        })}
        {r14Mode ? (
          <button
            className="r14-output-tile r14-output-tile--siren"
            type="button"
            data-tone={sirenPresentation.tone}
            data-state={siren.siren.state.toLowerCase()}
            disabled={!canOperate || commandsLocked}
            onClick={onOperateSiren}
            aria-label={`Sirene industrial: ${siren.siren.state === "ON" ? "desligar" : durationAccessibleLabel("testar", sirenDuration)}. Estado ${sirenPresentation.stateLabel}`}
          >
            <span className="r14-output-tile__siren-icon" aria-hidden="true"><R8Icon icon="industrial-siren" /></span>
            <span className="r14-output-tile__kind">SIREN_MAIN</span>
            <strong>{sirenPresentation.stateLabel}</strong>
            <small>{siren.siren.auto_off_at_utc ? `OFF ${formatEventTime(siren.siren.auto_off_at_utc)}` : `${sirenDuration / 1_000}s`}</small>
            <i aria-hidden="true" />
          </button>
        ) : (
          <article className="r8-output-card r8-output-card--siren" data-tone={sirenPresentation.tone}>
            <div className="r8-output-card__icon" aria-hidden="true"><R8Icon icon="industrial-siren" /></div>
            <div><span>SAÍDA CRÍTICA</span><h2>Sirene industrial</h2><p>{sirenPresentation.stateLabel}{siren.siren.auto_off_at_utc ? ` · auto-OFF ${formatEventTime(siren.siren.auto_off_at_utc)}` : ""}</p></div>
            <button type="button" data-action={siren.siren.state === "ON" ? "off" : "test"} disabled={!canOperate || commandsLocked} onClick={onOperateSiren} aria-label={`Sirene industrial: ${siren.siren.state === "ON" ? "desligar" : durationAccessibleLabel("testar", sirenDuration)}`}>{siren.siren.state === "ON" ? "DESLIGAR" : `TESTAR ${sirenDuration / 1000}s`}</button>
          </article>
        )}
      </div>

      <div className="r8-authority-note"><span aria-hidden="true"><R8CheckIcon /></span><p>A interface só confirma uma ação após versão avançada e pós-condição relida do ImerG.</p></div>
    </>
  );
}

export function R8EventsView({
  troubles,
  queue,
  reason,
  canOperate,
  commandsLocked,
  onReasonChange,
  onAcknowledge,
  r14Mode = false,
}: {
  troubles: TroubleState;
  queue: QueueState;
  reason: string;
  canOperate: boolean;
  commandsLocked: boolean;
  onReasonChange: (value: string) => void;
  onAcknowledge: (item: TroubleState["active"][number]) => void;
  r14Mode?: boolean;
}) {
  return (
    <>
      <ViewTitle eyebrow="EVENTOS" title="Linha do tempo" aside={<span className="r8-readback-badge">{troubles.counts.open} ABERTO{troubles.counts.open === 1 ? "" : "S"}</span>} />
      {r14Mode
        ? <p className="r8-view-intro">Somente falhas reais fornecidas pelo domínio atual. Incidentes ilustrativos, mapas e snapshots não são simulados.</p>
        : <p className="r8-view-intro">Somente Trouble real fornecido pelo domínio atual. Incidentes ilustrativos, mapas e snapshots não são simulados.</p>}
      <label className="r8-reason-field"><span>{r14Mode ? "Motivo para reconhecimento de falha" : "Motivo para reconhecimento de Trouble"}</span><input value={reason} maxLength={500} onChange={(event) => onReasonChange(event.target.value)} /></label>
      {troubles.active.length > 0 ? (
        <ol className="r8-event-timeline">
          {troubles.active.map((item) => (
            <li key={item.trouble_id} data-tone="failure">
              <time>{formatEventTime(item.updated_at_utc ?? item.opened_at_utc)}</time>
              <div><span>FALHA TÉCNICA · {item.severity}</span><h2>{item.message}</h2><p>{item.resource_id} · {item.code} · {item.occurrence_count} ocorrência(s)</p></div>
              {item.status === "OPEN" && canOperate ? <button type="button" disabled={commandsLocked} onClick={() => onAcknowledge(item)}>RECONHECER</button> : <strong>{item.status}</strong>}
            </li>
          ))}
        </ol>
      ) : (
        <section className="r8-empty-timeline"><span aria-hidden="true"><R8CheckIcon /></span><h2>{r14Mode ? "Sem falha ativa" : "Sem Trouble ativo"}</h2><p>Nenhum evento fictício foi inserido para preencher a linha do tempo.</p></section>
      )}
      {Object.values(queue.counts).some((value) => value > 0) ? (
        <section className="r8-queue-summary"><span>FILA TÉCNICA</span><strong>{Object.entries(queue.counts).map(([key, value]) => `${queueCountLabel(key)} ${value}`).join(" · ")}</strong></section>
      ) : null}
    </>
  );
}

function systemTone(ok: boolean): "armed" | "failure" {
  return ok ? "armed" : "failure";
}

export function R8SystemView({
  cockpit,
  resources,
  outputs,
  userName,
  role,
  siteId,
  stage,
  onRefresh,
}: {
  cockpit: CockpitState;
  resources: RegistrySummary;
  outputs: RegistrySummary;
  userName: string;
  role: string;
  siteId: string;
  stage: string;
  onRefresh: () => void;
}) {
  const relayConfirmed = cockpit.relay.connected
    && (cockpit.relay.output_state_confirmed ?? cockpit.relay.all_off_confirmed);
  const watchdogHealthy = cockpit.external_watchdog.status.trim().toUpperCase() === "HEALTHY"
    && cockpit.external_watchdog.freshness.trim().toUpperCase() === "FRESH";
  const restoreDrillStatus = cockpit.data_protection.last_backup?.restore_drill_status ?? "—";
  const backupHealthy = cockpit.data_protection.backup_freshness.trim().toUpperCase() === "FRESH"
    && restoreDrillStatus.trim().toUpperCase() === "PASSED";
  const health = [
    {
      label: "Core e SQLite",
      value: cockpit.core.database_ok ? "ÍNTEGRO" : "FALHA",
      detail: `replay físico ${localizedTechnicalState(cockpit.core.replay_physical_output)}`,
      ok: cockpit.core.database_ok,
    },
    {
      label: "Watchdog externo",
      value: localizedTechnicalState(cockpit.external_watchdog.status),
      detail: `${localizedTechnicalState(cockpit.external_watchdog.freshness)} · ${Math.round(cockpit.external_watchdog.age_seconds)}s`,
      ok: watchdogHealthy,
    },
    {
      label: "Backup e restore",
      value: localizedTechnicalState(cockpit.data_protection.backup_freshness),
      detail: `drill ${localizedTechnicalState(restoreDrillStatus)}`,
      ok: backupHealthy,
    },
    {
      label: "Automação de proteção",
      value: cockpit.protection_automation.running ? "EM EXECUÇÃO" : "PARADA",
      detail: cockpit.protection_automation.last_error ?? `${cockpit.protection_automation.completed_jobs} jobs concluídos`,
      ok: cockpit.protection_automation.running && !cockpit.protection_automation.last_error,
    },
    {
      label: "Módulo de relés",
      value: relayConfirmed ? "CONFIRMADO" : "SEM CONFIRMAÇÃO",
      detail: !relayConfirmed
        ? cockpit.relay.connected
          ? "estado de saída não confirmado"
          : "módulo desconectado"
        : (cockpit.relay.latched_relays?.length ?? 0) > 0
          ? `LATCH R${cockpit.relay.latched_relays?.join(", R")}`
          : "ALL OFF",
      ok: relayConfirmed,
    },
    {
      label: "Telegram",
      value: cockpit.telegram.ready && !cockpit.telegram.degraded ? "PRONTO" : "DEGRADADO",
      detail: `${cockpit.telegram.delivery_counts.total} entregas`,
      ok: cockpit.telegram.ready && !cockpit.telegram.degraded,
    },
    {
      label: "Storage",
      value: cockpit.data_protection.storage.capacity_status,
      detail: `${Math.round(cockpit.data_protection.storage.free_bytes / 1024 ** 3)} GB livres`,
      ok: cockpit.data_protection.storage.capacity_status === "OK",
    },
  ];

  return (
    <>
      <ViewTitle
        eyebrow="SISTEMA"
        title="Confiança técnica"
        aside={<button className="r8-icon-action" type="button" onClick={onRefresh} aria-label="Atualizar sistema"><R8RefreshIcon /></button>}
      />

      <section className="r8-operator-card">
        <div aria-hidden="true">{userName.slice(0, 1).toUpperCase()}</div>
        <span><strong>{userName}</strong><small>Telegram · {role} · {siteId}</small></span>
        <b>{stage}</b>
      </section>

      <section className="r8-system-health" aria-label="Saúde técnica">
        {health.map((item) => (
          <article key={item.label} data-tone={systemTone(item.ok)}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <small>{item.detail}</small>
          </article>
        ))}
      </section>

      <section className="r8-registry-stack" aria-label="Registries autoritativos separados">
        <article>
          <span className="r8-kicker">RESOURCE REGISTRY</span>
          <strong>{resources.resource_count ?? 0}<small> recursos</small></strong>
          <p>{Object.keys(resources.kinds ?? {}).length} tipos · revisão {resources.revision}</p>
          <code>{resources.sha256}</code>
        </article>
        <article>
          <span className="r8-kicker">OUTPUT REGISTRY</span>
          <strong>{outputs.output_count ?? 0}<small> saídas</small></strong>
          <p>mutação bloqueada · revisão {outputs.revision}</p>
          <code>{outputs.sha256}</code>
        </article>
      </section>

      <section className="r8-separation-note">
        <strong>DOMÍNIOS PRESERVADOS</strong>
        <p>Os registries permanecem autoritativos e separados. Esta interface não infere vínculos entre R01–R32, bypass, PGM ou sirene.</p>
      </section>
    </>
  );
}
